import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Interfaces
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IUpdatedPayPeriodsProviders } from "@/Washouts/helpers/PayPeriodsProviders/interfaces";
import { IUsePayPeriodsProvidersArgs } from "./interfaces";

// API
import { onUpdatePeriodPayrollProviders } from "@/Washouts/api/PayPeriodsProviders/subscriptions";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Helpers
import { onUpdatePayPeriodsProvidersCallback } from "@/Washouts/helpers/PayPeriodsProviders";

export const usePayPeriodsProviders = (args: Readonly<IUsePayPeriodsProvidersArgs>) => {
	const { locationId = "", payPeriodId = "", onExecutedSubscription = () => {} } = args ?? {};

	// Cache client
	const queryClient = useQueryClient();

	// Subscriptions
	const { getSubscription, addSubscription } = useSubscriptions();

	const identifier = useMemo(() => ["updatePeriodPayrollProviders", locationId], [locationId]);

	const subscription = getSubscription(identifier);

	subscription?.subscriptions?.onUpdatePeriodPayrollProviders?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdatePeriodPayrollProviders", IUpdatedPayPeriodsProviders>) => {
			onUpdatePayPeriodsProvidersCallback({ queryClient, response });

			// Use optional response data
			const data = response.data?.onUpdatePeriodPayrollProviders ?? null;
			onExecutedSubscription(data);
		}
	);

	useEffect(() => {
		if (!locationId) {
			return () => {};
		}

		addSubscription(identifier, [
			{
				context: "onUpdatePeriodPayrollProviders",
				query: onUpdatePeriodPayrollProviders,
				variables: {
					locationId,
				},
			},
		]);

		return () => {};
	}, [locationId, payPeriodId]);
};
